import type { RouteLocationRaw } from 'vue-router'
import { useUserStore } from '~/stores/user'
import { useUserWorkspacesStore } from '~/stores/userWorkspaces'

// Back-navigation target for the org-settings and system-admin navs. Their back
// link used to always go to the workspace chooser (`index`); instead return the
// user to the workspace dashboard they most recently visited (TRV-1206), falling
// back to the chooser when there's no resolvable recent workspace (e.g. the
// user opened System Admin straight from the chooser). Recency is per tab
// (sessionRecentWorkspaceIds, not the all-time localStorage list), so a direct
// entry in a fresh tab also falls back to the chooser rather than pointing at
// a workspace from some earlier session or another tab.
//
// `preferOrganizationSlug` (org settings) restricts the choice to workspaces in
// that org; omit it (system admin) to use the most recent across all orgs.
export function useSettingsBackDestination(
  preferOrganizationSlug?: Ref<string>,
) {
  const userStore = useUserStore()
  const userWorkspacesStore = useUserWorkspacesStore()

  // A recent workspace id resolved against the user's current memberships;
  // null when it no longer resolves (deleted workspace, lost access, no org).
  function resolveWorkspace(workspaceId: string) {
    const organizationSlug =
      userStore.getOrganizationSlugForWorkspace(workspaceId)
    const workspace = userStore.workspaces.find((ws) => ws.id === workspaceId)

    if (!organizationSlug || !workspace) return null

    return { workspaceId, organizationSlug, title: workspace.title }
  }

  // When an org preference is given (org settings), filter to it — including
  // the `''` defensive fallback, which matches no real org and so falls
  // through to the chooser rather than returning a wrong-org workspace. A
  // truthy check would treat `''` as "no preference" and leak another org.
  function matchesOrganizationPreference(organizationSlug: string) {
    const preferredSlug = preferOrganizationSlug?.value
    return preferredSlug === undefined || organizationSlug === preferredSlug
  }

  const backWorkspace = computed(
    () =>
      userWorkspacesStore.sessionRecentWorkspaceIds
        .map(resolveWorkspace)
        .filter((entry) => entry !== null)
        .find((entry) =>
          matchesOrganizationPreference(entry.organizationSlug),
        ) ?? null,
  )

  const backTo = computed<RouteLocationRaw>(() =>
    backWorkspace.value
      ? {
          name: 'organizationSlug-workspaceId',
          params: {
            organizationSlug: backWorkspace.value.organizationSlug,
            workspaceId: backWorkspace.value.workspaceId,
          },
        }
      : { name: 'index' },
  )

  const backLabel = computed(() =>
    backWorkspace.value ? backWorkspace.value.title : 'All workspaces',
  )

  return { backTo, backLabel }
}
